import React, { useEffect, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CardBody, Button } from '@windmill/react-ui';
import { useGamification } from '../../context/GamificationContext';
import { getBadgeIcon } from './BadgeIcons';
import { AuthContext } from '../../context/AuthContext';

const AchievementToast = () => {
  const { isToastVisible, toastData, hideToast } = useGamification();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const [animationState, setAnimationState] = useState('idle'); // 'idle', 'entering', 'exiting'
  const [currentToastData, setCurrentToastData] = useState(null);
  
  // Handle toast appearance and disappearance with proper animations
  useEffect(() => {
    if (isToastVisible && toastData) {
      setCurrentToastData(toastData);
      setAnimationState('entering');
    } else if (!isToastVisible && animationState === 'entering') {
      // Start exit animation
      setAnimationState('exiting');
      
      // After exit animation completes, reset state
      const exitTimer = setTimeout(() => {
        setAnimationState('idle');
        setCurrentToastData(null);
      }, 500); // Match to the exit animation duration
      
      return () => clearTimeout(exitTimer);
    }
  }, [isToastVisible, toastData, animationState]);
  
  // Create confetti effect on mount
  useEffect(() => {
    if (animationState === 'entering') {
      // We would implement a confetti effect here
      // Using a library like canvas-confetti
    }
  }, [animationState]);
  
  const handleViewAllClick = () => {
    hideToast();
    history.push('/app/achievements');
  };
  
  // Check if this is an upgrade to an existing badge
  const isUpgrade = () => {
    if (!user?.badges || !currentToastData) return false;
    
    // Filter out the current badge being shown
    const existingBadges = user.badges.filter(
      badge => badge.id === currentToastData.id && 
              badge._id !== currentToastData._id && 
              badge.tier < currentToastData.tier
    );
    
    return existingBadges.length > 0;
  };
  
  // Only render if there's toast data and we're not in the idle state
  if (animationState === 'idle' || !currentToastData) {
    return null;
  }
  
  return (
    <div className={`badge-toast ${animationState}`}>
      <Card colored className="bg-white dark:bg-gray-800 shadow-lg border-l-4 border-purple-500">
        <CardBody>
          <div className="flex items-start">
            <div className="mr-4 flex-shrink-0">
              <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-full">
                {getBadgeIcon(currentToastData.id, currentToastData.tier, 'h-10 w-10 text-purple-600 dark:text-purple-300')}
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isUpgrade() ? `${currentToastData.name} Upgraded!` : currentToastData.name}
                </h3>
                <button 
                  onClick={hideToast}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {isUpgrade() 
                  ? `Congratulations! Your ${currentToastData.name} badge has been upgraded to ${currentToastData.tierDetails?.requirement || 'a higher tier'}.`
                  : (currentToastData.tierDetails ? currentToastData.tierDetails.requirement : currentToastData.description)
                }
              </p>
              
              <div className="mt-3 flex justify-end">
                <Button size="small" onClick={handleViewAllClick}>
                  View All Badges
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// Add to global CSS:
const globalStyles = `
@keyframes fadeInSlide {
  0% { opacity: 0; transform: translateX(30px); }
  100% { opacity: 1; transform: translateX(0); }
}

.animate-fadeInSlide {
  animation: fadeInSlide 0.3s ease-out forwards;
}
`;

export default AchievementToast; 